export const THERAPIST_THEME = {
  colors: {
    primary: '#4a6fa5',      // MSB Blue
    secondary: '#6b96c9',    // MSB Light Blue
    accent: '#f29057',       // MSB Orange
    background: '#f5f7fa',   // MSB Light Gray
    surface: '#ffffff',      // White
    text: '#333844',         // MSB Dark Gray
    textMuted: '#64748b',    // MSB Medium Gray
    success: '#059669',      // Green
    warning: '#d97706',      // Orange
    error: '#dc2626',        // Red
    info: '#0284c7',         // Blue
    // MSB Brand Colors
    msbBlue: '#4a6fa5',
    msbLightBlue: '#6b96c9',
    msbOrange: '#f29057',
    msbSkyBlue: '#87CEEB',
    msbDarkBlue: '#5F9EA0',
  },
  fonts: {
    family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    sizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
  },
} as const;

export const CHILD_THEME = {
  colors: {
    primary: '#87CEEB',      // MSB Sky Blue
    secondary: '#5F9EA0',     // MSB Dark Blue
    accent: '#87CEEB',        // MSB Sky Blue accent
    pink: '#ec4899',
    green: '#10b981',
    orange: '#f97316',
    background: '#fff',       // Clean white
    surface: '#ffffff',
    card: '#fff',             // Clean white cards
    text: '#000',             // Black text
    textLight: '#87CEEB',     // MSB Sky Blue for light text
    // Authentic MSB Brand Colors
    msbSkyBlue: '#87CEEB',
    msbDarkBlue: '#5F9EA0',
    msbLightBlue: '#ADD8E6',
    msbWhite: '#fff',
    msbBlack: '#000',
    msbRed: '#dc143c',        // MSB highlight red
  },
  fonts: {
    family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
    sizes: {
      sm: '1rem',
      base: '1.125rem',
      lg: '1.25rem',
      xl: '1.5rem',
      '2xl': '1.75rem',
      '3xl': '2rem',
      '4xl': '2.5rem',
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1.25rem',
    lg: '2rem',
    xl: '2.5rem',
    '2xl': '3.5rem',
  },
  borderRadius: {
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.5rem',
    '2xl': '2rem',
    '3xl': '3rem',
  },
} as const;

export type ThemeType = 'therapist' | 'child';

export const getTheme = (themeType: ThemeType) => {
  return themeType === 'therapist' ? THERAPIST_THEME : CHILD_THEME;
};

// CSS Custom Properties for themes
export const generateCSSVariables = (themeType: ThemeType) => {
  const theme = getTheme(themeType);
  const prefix = themeType;
  
  const cssVars: Record<string, string> = {};
  
  // Colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    cssVars[`--${prefix}-${key}`] = value;
  });
  
  // Font sizes
  Object.entries(theme.fonts.sizes).forEach(([key, value]) => {
    cssVars[`--${prefix}-text-${key}`] = value;
  });
  
  // Spacing
  Object.entries(theme.spacing).forEach(([key, value]) => {
    cssVars[`--${prefix}-space-${key}`] = value;
  });
  
  // Border radius
  Object.entries(theme.borderRadius).forEach(([key, value]) => {
    cssVars[`--${prefix}-radius-${key}`] = value;
  });
  
  return cssVars;
};